/**
 * Armor icon family — World Art Pack 2, Task 5.
 *
 * One procedural generator per armor `IconKind` (12 kinds, covering every
 * `type === 'armor'` item in ITEM_DATABASE). Each painter draws flat,
 * silhouette-first inner art in the central 24x24 space per
 * `docs/design/coil-art/ICON_DESIGN_SHEETS.md` (2-3 value steps, top-left
 * light). Armor is front-facing — no hero diagonal (weaponIcons.ts `diag()`
 * deliberately not mirrored here). `framedIcon` owns ALL framing (rarity
 * ring, top-tier halo) and returns the base64 data URL the ladder expects.
 *
 * MATERIAL PALETTE DECISION: `ItemDefinition` exposes a REAL armor-class
 * signal — live `tags` carry `cloth` (56 items) / `leather` (38) / `plate`
 * (34) / `mail` (34), i.e. 162/220 armor items. So the 8 wearable-slot kinds
 * (helm/chest/legs/boots/gloves/shoulders/belt/cloak) read a 2-value palette
 * (base + top-left light, plus a per-class dark trim) selected by tag:
 * plate|mail -> plate grey, leather -> brown, cloth -> blue-grey. Items
 * without a class tag fall back to the same keywords in id/name — extended
 * with tier-set/class-family proper nouns (rogue/icemage/hunter/greenwarden/
 * duskweaver/arcanist/worldtree) that name a set rather than a material.
 * Items with neither a tag nor a keyword hit fall back to a fourth tier,
 * `requiredClass` (mage/priest/warlock/shaman -> cloth, hunter/rogue/druid
 * -> leather, warrior/paladin -> plate); with no signal at all they default
 * to neutral steel (the plate greys). The 4
 * accessory kinds (ring/amulet/trinket/offhand) use fixed gold/steel/book
 * palettes — material class carries no visual meaning for jewelry or a tome.
 *
 * Rarity/tint accents: the ring's gem takes the rarity color at >= uncommon
 * (neutral grey gem at common — the frame already carries white). `look.tint`
 * (elemental keyword inference from iconLook.ts) colors the amulet's pendant
 * stone and the tome's cover emblem; every other kind ignores it.
 *
 * Registration happens in ItemIconRegistry.ts (the single composition seam);
 * this file only imports a TYPE from the registry, so there is no runtime
 * import cycle.
 */
import { framedIcon } from './rarityFrame';
import { inferIconLook, type IconKind, type IconLook } from './iconLook';
import { rarityColor } from '@/game/ui/components/itemkit/shared';
import type { ProceduralIconGenerator } from '../ItemIconRegistry';
import type { ItemDefinition } from '@/shared/content/generated/items.gen';

// --- armor-class material palettes (see header) -----------------------------

export interface ArmorMaterialPalette {
  base: string;
  light: string;
  dark: string;
}

export const ARMOR_MATERIALS: Readonly<Record<'plate' | 'leather' | 'cloth', ArmorMaterialPalette>> = {
  plate: { base: '#9ba1ad', light: '#c9ccd4', dark: '#6e747f' },
  leather: { base: '#7a5a2e', light: '#a07a3e', dark: '#5a4020' },
  cloth: { base: '#6f7f9e', light: '#96a6c6', dark: '#4f5b74' },
};

const CLOTH_WORDS = /cloth|silk|robe|linen|wool|icemage_|arcanist/;
const LEATHER_WORDS = /leather|hide|pelt|rogue_|hunter_|greenwarden|duskweaver|worldtree/;
const PLATE_WORDS = /plate|mail/;

/** requiredClass -> material, for items with neither a class tag nor an
 *  id/name keyword hit (tier-set pieces named after their set, not their
 *  material — e.g. apprentice_hat carries requiredClass:'mage' but no
 *  cloth-family word). Checked after the keyword ladder, before the final
 *  neutral-steel default. */
const CLASS_MATERIAL: Readonly<Record<string, 'cloth' | 'leather' | 'plate'>> = {
  mage: 'cloth', priest: 'cloth', warlock: 'cloth', shaman: 'cloth',
  hunter: 'leather', rogue: 'leather', druid: 'leather',
  warrior: 'plate', paladin: 'plate',
};

/** Tag hit -> id/name keyword hit -> requiredClass hit -> neutral steel (plate greys). */
function inferMaterial(item: ItemDefinition): ArmorMaterialPalette {
  const tags = item.tags ?? [];
  if (tags.includes('cloth')) return ARMOR_MATERIALS.cloth;
  if (tags.includes('leather')) return ARMOR_MATERIALS.leather;
  if (tags.includes('plate') || tags.includes('mail')) return ARMOR_MATERIALS.plate;
  const text = `${item.id} ${item.name ?? ''}`.toLowerCase();
  if (CLOTH_WORDS.test(text)) return ARMOR_MATERIALS.cloth;
  if (LEATHER_WORDS.test(text)) return ARMOR_MATERIALS.leather;
  if (PLATE_WORDS.test(text)) return ARMOR_MATERIALS.plate;
  const byClass = item.requiredClass ? CLASS_MATERIAL[item.requiredClass] : undefined;
  if (byClass) return ARMOR_MATERIALS[byClass];
  return ARMOR_MATERIALS.plate; // neutral steel default
}

// --- shared accent constants (accessory kinds + buckles/clasps) -------------
const GOLD = '#d4a940';
const GOLD_LIGHT = '#f0cc6e';
const GOLD_DARK = '#a37c2c';
const PAGE = '#e8e4da';
const WATCH_FACE = '#eef1f6';
const WATCH_HANDS = '#4a4e58';
/** Neutral gem for common-rarity rings (>= uncommon takes the rarity hue). */
export const RING_GEM_NEUTRAL = '#b9bfca';
/** Default amulet-stone / tome-emblem arcane violet when no elemental tint. */
const STONE_DEFAULT = '#b98bff';

/** Rarity tiers whose ring gem takes the rarity color ('unique' is the
 *  declared top-tier spelling; real content uses 'legendary' — support both,
 *  same as rarityFrame's halo gate). */
const GEM_TIERS = new Set<string>(['uncommon', 'rare', 'epic', 'legendary', 'unique']);

type KindPainter = (look: IconLook, mat: ArmorMaterialPalette, size: number) => string;

// --- helm — dome + nose guard + cheek guards (eye-slit gaps) over a brim ----
const helm: KindPainter = (look, mat, size) => framedIcon(`
    <path d="M4.8 12.8 L4.8 11.6 Q4.8 4.4 12 4.4 Q19.2 4.4 19.2 11.6 L19.2 12.8 Z" fill="${mat.base}"/>
    <path d="M6.6 9.6 Q7.2 6.4 10.6 5.4 L10.6 7.3 Q8.4 8 7.9 10.2 Z" fill="${mat.light}"/>
    <rect x="4.2" y="12.6" width="15.6" height="2.2" rx="0.9" fill="${mat.dark}"/>
    <path d="M5.6 14.8 L9.5 14.8 L9.2 18.4 Q7 18 5.8 16.4 Z" fill="${mat.base}"/>
    <path d="M14.5 14.8 L18.4 14.8 L18.2 16.4 Q17 18 14.8 18.4 Z" fill="${mat.base}"/>
    <rect x="10.7" y="13.6" width="2.6" height="6.6" rx="1.1" fill="${mat.base}"/>
    <rect x="10.7" y="13.6" width="1.1" height="6.6" rx="0.55" fill="${mat.light}" opacity="0.55"/>
  `, look.rarity, size);

// --- chest — cuirass: wide shoulder bar, arm scoops, waisted torso, collar ---
const chest: KindPainter = (look, mat, size) => framedIcon(`
    <path d="M4 5.2 L20 5.2 L19.6 9 Q17.8 9.6 17.4 11.8 L17.2 15 Q16.8 19.4 12 20.6 Q7.2 19.4 6.8 15 L6.6 11.8 Q6.2 9.6 4.4 9 Z" fill="${mat.base}"/>
    <path d="M4 5.2 L8.6 5.2 L8.4 11 L6.6 11.8 Q6.2 9.6 4.4 9 Z" fill="${mat.light}"/>
    <path d="M8.9 5.2 Q12 9.2 15.1 5.2 Z" fill="${mat.dark}"/>
    <rect x="11.55" y="9.8" width="0.9" height="8.6" fill="${mat.dark}" opacity="0.6"/>
    <path d="M7.2 16 Q12 18 16.8 16 L16.5 17.8 Q12 19.8 7.5 17.8 Z" fill="${mat.dark}"/>
  `, look.rarity, size);

// --- legs — greave pair hanging from a waistband -----------------------------
const legs: KindPainter = (look, mat, size) => framedIcon(`
    <rect x="5.2" y="4.2" width="13.6" height="2.6" rx="1" fill="${mat.dark}"/>
    <path d="M5.6 7 L11 7 L10.4 20.8 L6.4 20.8 Z" fill="${mat.base}"/>
    <path d="M13 7 L18.4 7 L17.6 20.8 L13.6 20.8 Z" fill="${mat.base}"/>
    <path d="M5.6 7 L7.6 7 L7.3 20.8 L6.4 20.8 Z" fill="${mat.light}"/>
    <path d="M13 7 L15 7 L14.7 20.8 L13.6 20.8 Z" fill="${mat.light}" opacity="0.55"/>
    <rect x="6.5" y="13.2" width="4.2" height="1.1" fill="${mat.dark}" opacity="0.7"/>
    <rect x="13.4" y="13.2" width="4.2" height="1.1" fill="${mat.dark}" opacity="0.7"/>
  `, look.rarity, size);

// --- boots — single boot side profile (shaft + toe to the right) -------------
const boots: KindPainter = (look, mat, size) => framedIcon(`
    <path d="M6.8 5.4 L12.8 5.4 L12.8 13.6 L17.6 13.6 Q19.8 13.6 19.8 16.2 L19.8 19 L6.8 19 Z" fill="${mat.base}"/>
    <rect x="6.2" y="4.4" width="7.2" height="2.4" rx="1" fill="${mat.dark}"/>
    <rect x="7.7" y="7.6" width="1.7" height="10" fill="${mat.light}"/>
    <path d="M6.2 18.4 L20.4 18.4 L20.4 19.6 Q20.4 20.4 19.6 20.4 L7 20.4 Q6.2 20.4 6.2 19.6 Z" fill="${mat.dark}"/>
  `, look.rarity, size);

// --- trail — a wisp of glowing motes sweeping up-right (lane BD, D46) ---------
// The Trail slot's item styles the ghost's light trail, so its icon IS a trail:
// motes in the item's authored trail colour (look.tint) on a soft halo.
const TRAIL_DEFAULT = '#d8f4ff';
const trail: KindPainter = (look, _mat, size) => {
  const c = look.tint ?? TRAIL_DEFAULT;
  return framedIcon(`
    <path d="M4.4 18.6 Q10 17.6 13.6 13.4 Q16.4 10.2 19.4 7.2" fill="none" stroke="${c}" stroke-width="1.1" stroke-linecap="round" opacity="0.35"/>
    <circle cx="17.6" cy="8.4" r="4.6" fill="${c}" opacity="0.22"/>
    <circle cx="17.6" cy="8.4" r="2.9" fill="${c}"/>
    <circle cx="16.8" cy="7.6" r="0.9" fill="#ffffff" opacity="0.8"/>
    <circle cx="12.9" cy="13.4" r="2.2" fill="${c}" opacity="0.75"/>
    <circle cx="8.9" cy="16.4" r="1.6" fill="${c}" opacity="0.5"/>
    <circle cx="5.6" cy="18.2" r="1.1" fill="${c}" opacity="0.3"/>
  `, look.rarity, size);
};

// --- lantern — a small hanging lantern lit in the item's colour (lane BK, B26) -
// The Lantern slot's item is the light the ghost carries; its icon shows the
// lantern itself, glass and glow in the authored lantern colour (look.tint).
const LANTERN_DEFAULT = '#ffc46b';
const lantern: KindPainter = (look, _mat, size) => {
  const c = look.tint ?? LANTERN_DEFAULT;
  return framedIcon(`
    <circle cx="12" cy="13.6" r="7.4" fill="${c}" opacity="0.2"/>
    <path d="M9.6 6.2 Q12 2.6 14.4 6.2" fill="none" stroke="${GOLD_LIGHT}" stroke-width="1.1" stroke-linecap="round"/>
    <rect x="8.6" y="6" width="6.8" height="2" rx="0.8" fill="${GOLD}"/>
    <rect x="8.9" y="8" width="6.2" height="9.4" rx="1.6" fill="${c}" opacity="0.85"/>
    <ellipse cx="12" cy="12.8" rx="1.7" ry="2.5" fill="#ffffff" opacity="0.85"/>
    <rect x="11.5" y="8" width="1" height="9.4" fill="${GOLD}" opacity="0.7"/>
    <rect x="8.2" y="17.2" width="7.6" height="2" rx="0.8" fill="${GOLD}"/>
  `, look.rarity, size);
};

// --- gloves — gauntlet back: fingers + back plate + thumb + flared cuff ------
const gloves: KindPainter = (look, mat, size) => framedIcon(`
    <rect x="5.9" y="5.6" width="2.3" height="5.4" rx="1.1" fill="${mat.light}"/>
    <rect x="8.6" y="4.4" width="2.3" height="6.4" rx="1.1" fill="${mat.base}"/>
    <rect x="11.3" y="4.2" width="2.3" height="6.6" rx="1.1" fill="${mat.base}"/>
    <rect x="14" y="5.2" width="2.3" height="5.8" rx="1.1" fill="${mat.base}"/>
    <rect x="5.9" y="9.6" width="10.4" height="7" rx="2" fill="${mat.base}"/>
    <rect x="6.5" y="10.2" width="2" height="5.6" rx="1" fill="${mat.light}" opacity="0.6"/>
    <rect x="16" y="10" width="2.7" height="5" rx="1.3" transform="rotate(-26 17.3 12.5)" fill="${mat.base}"/>
    <circle cx="9.3" cy="12" r="0.75" fill="${mat.dark}"/>
    <circle cx="11.7" cy="12" r="0.75" fill="${mat.dark}"/>
    <circle cx="14.1" cy="12" r="0.75" fill="${mat.dark}"/>
    <path d="M6 16.2 L17.6 16.2 L19 20.2 L4.6 20.2 Z" fill="${mat.dark}"/>
  `, look.rarity, size);

// --- shoulders — pauldron pair, right shell overlapping the left -------------
const shoulders: KindPainter = (look, mat, size) => framedIcon(`
    <path d="M3.2 15.6 Q2.6 8 9.6 6.4 Q12.8 8.2 13.4 11.6 Q13.7 13.8 13 15.6 Q8 17.2 3.2 15.6 Z" fill="${mat.base}"/>
    <path d="M4.4 9.8 Q5.6 7.4 8.6 6.7 L9.4 8.2 Q6.6 8.8 5.8 10.8 Z" fill="${mat.light}"/>
    <path d="M4 12.6 Q8.4 11 12.9 12.2" fill="none" stroke="${mat.dark}" stroke-width="1.1"/>
    <path d="M10.8 15.9 Q10.4 7.6 17.2 5.8 Q20.8 8 21.2 12 Q21.4 14.2 20.6 15.7 Q15.4 17.4 10.8 15.9 Z" fill="${mat.base}" stroke="${mat.dark}" stroke-width="0.9"/>
    <path d="M12.2 9.4 Q13.6 7 16.6 6.2 L17.4 7.8 Q14.6 8.4 13.6 10.6 Z" fill="${mat.light}"/>
    <path d="M11.6 12.9 Q16 11.2 20.8 12.5" fill="none" stroke="${mat.dark}" stroke-width="1.1"/>
  `, look.rarity, size);

// --- belt — strap band + gold buckle close-up --------------------------------
const belt: KindPainter = (look, mat, size) => framedIcon(`
    <rect x="2.6" y="9.4" width="18.8" height="5.8" rx="1" fill="${mat.base}"/>
    <rect x="2.6" y="9.4" width="18.8" height="1.5" rx="0.75" fill="${mat.light}"/>
    <rect x="2.6" y="14" width="18.8" height="1.2" fill="${mat.dark}"/>
    <circle cx="17.4" cy="12.3" r="0.65" fill="${mat.dark}"/>
    <circle cx="19.4" cy="12.3" r="0.65" fill="${mat.dark}"/>
    <rect x="8.4" y="7.9" width="7.2" height="8.8" rx="1.6" fill="none" stroke="${GOLD}" stroke-width="2"/>
    <rect x="8.4" y="7.9" width="3" height="1.6" rx="0.8" fill="${GOLD_LIGHT}"/>
    <rect x="11.5" y="8.8" width="1" height="7" rx="0.5" fill="${GOLD}"/>
  `, look.rarity, size);

// --- cloak — draped triangle w/ wavy hem + gold clasp ------------------------
const cloak: KindPainter = (look, mat, size) => framedIcon(`
    <path d="M4.6 6.2 Q12 3.8 19.4 6.2 L16.2 19.4 Q14.8 17.4 13.4 19.8 Q12 17.6 10.6 19.8 Q9.2 17.4 7.8 19.4 Z" fill="${mat.base}"/>
    <path d="M4.6 6.2 Q7.6 5.2 10.6 5 L9.9 18.2 Q9.2 17.4 7.8 19.4 Z" fill="${mat.light}"/>
    <path d="M12.9 7.8 L12.5 18 L13.4 19.8 L13.8 7.9 Z" fill="${mat.dark}" opacity="0.55"/>
    <path d="M15.9 7.6 L15 17.4 L16.2 19.4 L16.9 7.9 Z" fill="${mat.dark}" opacity="0.55"/>
    <circle cx="12" cy="6.6" r="2.1" fill="${GOLD}"/>
    <circle cx="11.3" cy="5.9" r="0.7" fill="${GOLD_LIGHT}"/>
  `, look.rarity, size);

// --- ring — gold band + gem (gem takes rarity color at >= uncommon) ----------
const ring: KindPainter = (look, _mat, size) => {
  const gem = GEM_TIERS.has(look.rarity)
    ? (rarityColor(look.rarity) ?? RING_GEM_NEUTRAL)
    : RING_GEM_NEUTRAL;
  return framedIcon(`
    <circle cx="12" cy="14.2" r="5.6" fill="none" stroke="${GOLD}" stroke-width="2.6"/>
    <path d="M7.2 11.8 Q8.6 9.6 11 9" fill="none" stroke="${GOLD_LIGHT}" stroke-width="1.2" stroke-linecap="round"/>
    <polygon points="12,3.2 15.6,7 12,10.8 8.4,7" fill="${gem}"/>
    <polygon points="12,3.2 12,10.8 8.4,7" fill="#ffffff" opacity="0.35"/>
  `, look.rarity, size);
};

// --- amulet — chain arc + pendant (stone takes look.tint) ---------------------
const amulet: KindPainter = (look, _mat, size) => {
  const stone = look.tint ?? STONE_DEFAULT;
  return framedIcon(`
    <path d="M4 4.4 Q12 11 20 4.4" fill="none" stroke="${GOLD}" stroke-width="1.6" stroke-dasharray="1.9 1.2" stroke-linecap="round"/>
    <circle cx="12" cy="8.6" r="1.2" fill="${GOLD}"/>
    <polygon points="12,9.4 15.8,13.6 12,19.8 8.2,13.6" fill="${GOLD}"/>
    <polygon points="12,9.4 12,19.8 8.2,13.6" fill="${GOLD_LIGHT}" opacity="0.55"/>
    <polygon points="12,11.4 14.1,13.7 12,17.2 9.9,13.7" fill="${stone}"/>
    <circle cx="11.3" cy="12.9" r="0.6" fill="#ffffff" opacity="0.7"/>
  `, look.rarity, size);
};

// --- trinket — pocket watch: loop + crown + gold case + hands -----------------
const trinket: KindPainter = (look, _mat, size) => framedIcon(`
    <circle cx="12" cy="4.4" r="1.5" fill="none" stroke="${GOLD}" stroke-width="1.2"/>
    <rect x="10.8" y="5.7" width="2.4" height="1.6" rx="0.6" fill="${GOLD_DARK}"/>
    <circle cx="12" cy="14" r="6.9" fill="${GOLD}"/>
    <path d="M6 11.4 Q7 8.6 9.8 7.5" fill="none" stroke="${GOLD_LIGHT}" stroke-width="1.4" stroke-linecap="round"/>
    <circle cx="12" cy="14" r="5.1" fill="${WATCH_FACE}"/>
    <rect x="11.55" y="10" width="0.9" height="4.4" rx="0.45" fill="${WATCH_HANDS}"/>
    <rect x="12" y="13.55" width="3.2" height="0.9" rx="0.45" fill="${WATCH_HANDS}"/>
    <circle cx="12" cy="14" r="0.85" fill="${GOLD_DARK}"/>
  `, look.rarity, size);

// --- offhand — leather-bound tome (emblem takes look.tint) --------------------
const offhand: KindPainter = (look, _mat, size) => {
  const emblem = look.tint ?? STONE_DEFAULT;
  return framedIcon(`
    <rect x="4.8" y="4.4" width="13.6" height="15.2" rx="1.2" fill="${ARMOR_MATERIALS.leather.base}"/>
    <rect x="4.8" y="4.4" width="2.4" height="15.2" rx="1" fill="${ARMOR_MATERIALS.leather.dark}"/>
    <rect x="18.4" y="5.4" width="1.6" height="13.2" rx="0.5" fill="${PAGE}"/>
    <rect x="7.8" y="5.2" width="9.4" height="1.3" fill="${ARMOR_MATERIALS.leather.light}"/>
    <rect x="4.8" y="7.4" width="2.4" height="1.2" fill="${GOLD}"/>
    <rect x="4.8" y="15.4" width="2.4" height="1.2" fill="${GOLD}"/>
    <circle cx="12.6" cy="12" r="2.9" fill="${emblem}"/>
    <circle cx="12.6" cy="12" r="1.5" fill="none" stroke="${GOLD_LIGHT}" stroke-width="0.8"/>
    <circle cx="11.7" cy="11.1" r="0.65" fill="#ffffff" opacity="0.7"/>
  `, look.rarity, size);
};

/** The 14 armor kinds this family owns (mirrors the IconKind armor row). */
export const ARMOR_ICON_KINDS: readonly IconKind[] = [
  'helm', 'chest', 'legs', 'boots', 'gloves', 'shoulders',
  'belt', 'cloak', 'ring', 'amulet', 'trinket', 'offhand', 'trail', 'lantern',
];

const PAINTERS: Readonly<Partial<Record<IconKind, KindPainter>>> = {
  helm, chest, legs, boots, gloves, shoulders, belt, cloak, ring, amulet, trinket, offhand, trail, lantern,
};

/** Adapt a look+material painter to the registry's (item, size) contract. */
const asGenerator = (paint: KindPainter): ProceduralIconGenerator =>
  (item: ItemDefinition, size: number) => paint(inferIconLook(item), inferMaterial(item), size);

export const ARMOR_KIND_ICONS: Partial<Record<IconKind, ProceduralIconGenerator>> =
  Object.freeze(Object.fromEntries(
    ARMOR_ICON_KINDS.map((kind) => [kind, asGenerator(PAINTERS[kind]!)]),
  ));
